import { collection, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import type { DailyRateLookup, FxRateSource, SupportedCurrency } from '@sentola/money';
import { moscowCalendarDay, precedingMoscowDays } from '@sentola/money';

const COLLECTION = 'dailyRates';
const PREFETCH_FALLBACK_DAYS = 7;

export type DailyRateDoc = {
    date: string;
    base: 'USD';
    rates: Partial<Record<SupportedCurrency, number>>;
    sources: Partial<Record<SupportedCurrency, FxRateSource>>;
    provider: string;
};

const sessionCache = new Map<string, DailyRateDoc | null>();

export async function fetchDailyRateDoc(day: string): Promise<DailyRateDoc | null> {
    if (sessionCache.has(day)) return sessionCache.get(day) ?? null;
    const snap = await getDoc(doc(db, COLLECTION, day));
    const value = snap.exists() ? (snap.data() as DailyRateDoc) : null;
    sessionCache.set(day, value);
    return value;
}

export async function buildRateLookup(days: readonly string[], currency: SupportedCurrency): Promise<DailyRateLookup> {
    const entries = new Map<string, { rate: number; source: FxRateSource }>();
    await Promise.all(
        days.map(async (day) => {
            const rateDoc = await fetchDailyRateDoc(day);
            const rate = rateDoc?.rates[currency];
            const source = rateDoc?.sources[currency];
            if (rate !== undefined && source !== undefined) {
                entries.set(day, { rate, source });
            }
        })
    );
    return (day) => entries.get(day);
}

// Shared so that a page rendering one balance per account issues a single query, not one per row.
let latestInFlight: Promise<DailyRateDoc | null> | null = null;

/**
 * The most recent day we hold rates for. Balances are marked to market, so they use this day
 * rather than any particular transaction's day.
 */
export function fetchLatestDailyRateDoc(): Promise<DailyRateDoc | null> {
    if (!latestInFlight) {
        latestInFlight = getDocs(query(collection(db, COLLECTION), orderBy('date', 'desc'), limit(1)))
            .then((snap) => {
                if (snap.empty) return null;
                const value = snap.docs[0].data() as DailyRateDoc;
                sessionCache.set(value.date, value);
                return value;
            })
            .catch((error) => {
                latestInFlight = null;
                throw error;
            });
    }
    return latestInFlight;
}

export async function prefetchRecentDailyRates(): Promise<void> {
    const today = moscowCalendarDay(new Date());
    const days = precedingMoscowDays(today, PREFETCH_FALLBACK_DAYS);
    await Promise.all([...days.map(fetchDailyRateDoc), fetchLatestDailyRateDoc()]);
}
